// Get language preference (secure default - no longer exposed in build)
const getPreferredLanguage = () => {
  return 'zh-CN'; // Default language for security
};

// Detect if we're on mobile Safari
const isMobileSafari = () => {
  return /Safari/i.test(navigator.userAgent) && 
         /Mobile/i.test(navigator.userAgent) && 
         !/Chrome/i.test(navigator.userAgent) && 
         !/CriOS/i.test(navigator.userAgent);
};

// Initialize speech synthesis for mobile Safari (must be called on user gesture)
export const initializeSpeechSynthesis = () => {
  if (isMobileSafari() && window.speechSynthesis) {
    // Better initialization that doesn't affect voice quality
    // Just trigger the synthesis engine without creating an audible utterance
    window.speechSynthesis.cancel(); // Clear any existing state
    
    // Create a very short, inaudible utterance to wake up the synthesis
    const utterance = new SpeechSynthesisUtterance(' ');
    utterance.volume = 0.01; // Very low volume instead of 0
    utterance.rate = 10; // Very fast
    utterance.onend = () => {
      // Immediately cancel to clean up
      window.speechSynthesis.cancel();
    };
    window.speechSynthesis.speak(utterance);
  }
};

// More robust text processing to extract Chinese content and remove English
const processChineseText = (text) => {
  if (!text) return '';
  
  try {
    // Split into sentences/segments
    const segments = text.split(/[.。!！?？\n]/);
    const chineseSegments = [];
    
    for (const segment of segments) {
      if (!segment.trim()) continue;
      
      // Check if this segment contains Chinese characters
      if (/[\u4E00-\u9FFF]/.test(segment)) {
        let processedSegment = segment.trim();
        
        // Remove English parts from the same line (common patterns)
        // Remove common English patterns that appear after Chinese
        processedSegment = processedSegment.replace(/\s*-\s*[A-Za-z].*/g, ''); // Remove "- English translation"
        processedSegment = processedSegment.replace(/\s*\|\s*[A-Za-z].*/g, ''); // Remove "| English translation"
        processedSegment = processedSegment.replace(/\s*\/\s*[A-Za-z].*/g, ''); // Remove "/ English translation"
        
        // Remove standalone English words/phrases at the end
        processedSegment = processedSegment.replace(/\s+[A-Za-z]+[\s\w]*$/g, '');
        
        // Remove excessive whitespace
        processedSegment = processedSegment.replace(/\s+/g, ' ').trim();
        
        if (processedSegment.length > 0) {
          chineseSegments.push(processedSegment);
        }
      }
    }
    
    return chineseSegments.join('。 '); // Join with Chinese period
  } catch (error) {
    return text; // Return original on error
  }
};

// Remove Pinyin from text before speaking (improved version)
const removePinyin = (text) => {
  if (!text) return '';
  
  try {
    // First process to get Chinese content and remove English
    let cleanText = processChineseText(text);
    
    // Then remove pinyin in parentheses
    cleanText = cleanText.replace(/\s*\([^)]*\)/g, '');
    
    // Clean up any remaining whitespace
    cleanText = cleanText.replace(/\s+/g, ' ').trim();
    
    return cleanText;
  } catch (error) {
    // In case of any error, return original text without attempting to process it
    return text;
  }
};

// Extract Chinese and English parts for display (more robust)
export const extractTranslation = (text) => {
  if (!text) return { original: '', translation: '' };
  
  try {
    const lines = text.split('\n');
    const chineseLines = [];
    const englishLines = [];
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;
      
      // Check if line contains Chinese characters
      if (/[\u4E00-\u9FFF]/.test(trimmedLine)) {
        // This line has Chinese - process it to remove English parts
        let chinesePart = trimmedLine;
        let englishPart = '';
        
        // Look for English after common separators
        const separators = [' - ', ' | ', ' / ', '  ']; // Two spaces also common
        for (const sep of separators) {
          if (chinesePart.includes(sep)) {
            const parts = chinesePart.split(sep);
            const potentialEnglish = parts[parts.length - 1];
            
            // If the last part is primarily English (no Chinese characters)
            if (!/[\u4E00-\u9FFF]/.test(potentialEnglish) && /[A-Za-z]/.test(potentialEnglish)) {
              englishPart = potentialEnglish.trim();
              chinesePart = parts.slice(0, -1).join(sep).trim();
              break;
            }
          }
        }
        
        chineseLines.push(chinesePart);
        if (englishPart) {
          englishLines.push(englishPart);
        }
      } else if (/[A-Za-z]/.test(trimmedLine)) {
        // This line is primarily English
        englishLines.push(trimmedLine);
      }
    }
    
    return {
      original: chineseLines.join('\n'),
      translation: englishLines.join('\n')
    };
  } catch (error) {
    return { original: text, translation: '' };
  }
};

// Speech synthesis (Text-to-Speech)
export const speak = (text, rate = 1, pitch = 1, voice = null) => {
  return new Promise((resolve, reject) => {
    if (!window.speechSynthesis) {
      reject('Speech synthesis not supported');
      return;
    }

    try {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      // Remove Pinyin and English before speaking
      const cleanText = removePinyin(text);
      
      if (!cleanText.trim()) {
        resolve(); // Nothing to speak
        return;
      }
      
      const utterance = new SpeechSynthesisUtterance(cleanText);
      
      // Set voice if provided, otherwise it will use the default
      if (voice) {
        utterance.voice = voice;
      } else {
        // Try to find a voice for the preferred language, otherwise use default
        const preferredLang = getPreferredLanguage();
        const voices = window.speechSynthesis.getVoices();
        const preferredVoice = voices.find(voice => voice.lang.includes(preferredLang.split('-')[0]));
        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }
      }

      utterance.rate = rate;
      utterance.pitch = pitch;
      
      utterance.onend = () => {
        resolve();
      };
      
      utterance.onerror = (event) => {
        reject(event.error);
      };
      
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      reject(error);
    }
  });
};

// Get available voices for speech synthesis
export const getVoices = () => {
  return new Promise((resolve) => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      resolve(voices);
    } else {
      // Wait for voices to be loaded
      window.speechSynthesis.onvoiceschanged = () => {
        resolve(window.speechSynthesis.getVoices());
      };
    }
  });
};

// Helper function to clean up repeated text patterns (simplified)
export const cleanTranscript = (text) => {
  if (!text) return '';
  
  // Remove excessive whitespace
  let cleaned = text.replace(/\s+/g, ' ').trim();
  
  // Remove simple repetitions (like "hello hello hello" -> "hello")
  const words = cleaned.split(' ');
  const result = [];
  
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    // Only add if it's not the same as the previous word (simple deduplication)
    if (i === 0 || word !== words[i - 1]) {
      result.push(word);
    }
  }
  
  return result.join(' ');
};

// Export mobile Safari detection for use in components
export { isMobileSafari }; 